import { action, makeObservable, observable } from 'mobx'

import WorkflowType from '~/client/src/shared/models/WorkflowType'

import BicRulesDraft, { BicRulesSnapshot } from './BicRulesDraft'

interface IFormTypeState {
  formType: WorkflowType
  bicRules: BicRulesSnapshot
}

/**
 * * While there are alternative methods for implementing Undo/Redo functionality,
 * * such as the Command pattern, this approach is straightforward.
 * * I've placed it here to prevent its use elsewhere until
 * * we establish a standardized approach for our application.
 */
export default class FormTypeStateHistory {
  @observable private states: IFormTypeState[] = []
  @observable private currentStateIdx: number = null

  public constructor(
    initialValue: WorkflowType,
    private readonly setFormType: (formType: WorkflowType) => void,
    private readonly bicRulesDraft: BicRulesDraft,
  ) {
    this.reInit(initialValue)
    makeObservable(this)
  }

  @action
  public reInit = (initialValue: WorkflowType) => {
    this.states = [this.makeState(initialValue)]
    this.currentStateIdx = 0
  }

  @action
  public captureState = (updatedType: WorkflowType) => {
    this.states.splice(
      ++this.currentStateIdx,
      this.states.length,
      this.makeState(updatedType),
    )
  }

  /** Rewrites every recorded state, for a change the server already made that undo must not take back. */
  @action
  public applyToAll = (update: (state: WorkflowType) => void) => {
    this.states.forEach(state => update(state.formType))
  }

  @action
  public undo = () => {
    if (this.hasUndo) {
      this.currentStateIdx--

      this.updateCurrentState()
    }
  }

  @action
  public undoToInitial = () => {
    if (this.states[0]) {
      this.currentStateIdx = 0

      this.updateCurrentState()
    }
  }

  @action
  public redo = () => {
    if (this.hasRedo) {
      this.currentStateIdx++

      this.updateCurrentState()
    }
  }

  @action
  public resetToInitial = () => {
    if (this.states.length > 1) {
      this.states = [this.states[0]]
      this.currentStateIdx = 0

      this.updateCurrentState()
    }
  }

  public get hasUndo(): boolean {
    return !!this.states[this.currentStateIdx - 1]
  }

  public get hasRedo(): boolean {
    return !!this.states[this.currentStateIdx + 1]
  }

  private makeState(formType: WorkflowType): IFormTypeState {
    return {
      formType: formType.getDeepCopy(),
      bicRules: this.bicRulesDraft.snapshot(),
    }
  }

  @action
  private updateCurrentState() {
    const { formType, bicRules } = this.states[this.currentStateIdx]

    // Rules first: setting the type re-renders the steps, which read their rules.
    this.bicRulesDraft.restore(bicRules)
    this.setFormType(formType.getDeepCopy())
  }
}
